import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Variant, DataScenario, ExportAction, ExportInProgressAction } from 'types/apis/cxEditor';
import { useGlobalConfig } from 'context/GlobalContext';
import { useSelector } from 'react-redux';
import { RootState } from 'store/Store';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { selectLevel1Data, selectLevel2Data } from 'store/dataReducers/featureMetricsSlice';
import { selectScenariosForTemplate, selectDataScenariosLoading, selectDataScenariosError } from 'store/dataReducers/dataScenariosSlice';
import { useAvailableFuelTypes } from 'hooks/useProjectFuelTypes';
import { mapMeasurementTypeToFuelType } from 'features/contentManagement/dataScenarios/dataScenariosUtils';
import { getFuelTypeFromTemplateKey } from 'apis/dataScenariosAPI';
import {
  getFuelTypeLabel,
  VALID_FUEL_TYPES,
  getHerFuelChipLabel,
  isHerDfherMonthlyReportTemplateKey,
  HER_DFHER_MONTHLY_REPORT_DISPLAY_NAME
} from 'utils/templates-from-feature-metrics';
import { createCxApiClient } from 'framework/http/CxEditorClient';
import { PermissionGate } from 'components/access/PermissionGate';
import { CAPABILITIES } from 'framework/access/capabilities';
import { ROUTE_TREE } from 'routes/AppRoutes';
import { SECTION_ID_TO_ELEMENT_KEY_MAP } from 'utils/html-section-parser';
import { WEB_TEMPLATE_TO_WIDGET_MAP, getWidgetIdFromTemplate, getWebWidgetDisplayName, isMultiWidgetTemplate } from 'constants/web-widget-mapping';
import {
  Box,
  Button,
  Select,
  MenuItem,
  FormControl,
  Typography,
  Tooltip,
  CircularProgress,
  Chip,
  Menu,
  ListItemText,
  Divider,
  alpha
} from '@mui/material';
import {
  Save,
  GitCompareArrows,
  FileClock,
  ClipboardCheck,
  Monitor,
  Smartphone,
  Download,
  Network,
  Languages,
  Layers,
  Library,
  Pipette,
  ChevronDown,
  PlusCircle,
  CheckCheck
} from 'lucide-react';

// Default locales fallback when project attributes are unavailable
const DEFAULT_LOCALES = ['en_US', 'es_US'];

const normalizeLocaleCode = (locale: string): string => {
  const input = String(locale || '').trim();
  if (!input) return '';
  const normalized = input.replace(/-/g, '_');
  const parts = normalized.split('_').filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0].toLowerCase()}_${parts[1].toUpperCase()}`;
  }
  return normalized;
};

const FUEL_TYPE_FULL_NAME: Record<string, string> = {
  ELECTRIC: 'Electric',
  GAS: 'Gas',
  WATER: 'Water',
  DUAL: 'Dual Fuel',
};

// EditorToolbar no longer makes API calls - moved to WysiwygPage to prevent duplicates

type Props = {
  templateKey: string;
  fuelType?: string; // Fuel type for Web templates (e.g., "GAS", "ELECTRIC")
  elementKey: string;
  variant: Variant;
  channel?: string;
  previewMode?: 'desktop' | 'mobile';
  disabled?: boolean; // Disable scenario/section changes while preview is rendering
  onElementChange: (k: string) => void;
  onVariantChange: (v: Variant) => void;
  onPreviewModeChange?: (mode: 'desktop' | 'mobile') => void;
  hoverInvestigateEnabled?: boolean;
  onHoverInvestigateChange?: (enabled: boolean) => void;
  onSave?: () => Promise<void> | void;
  onOpenCompare?: () => void;
  onCancel?: () => void;
  renderOnlyUpperSection?: boolean;
  renderOnlyLowerSection?: boolean;
  availableElements?: Array<{ elementKey: string; displayName: string }>; // Elements parsed from rendered HTML (optional)
  onRenderComplete?: (templateKey: string) => void; // Callback when render completes to trigger template/scenario fetch
  modifiedCount?: number; // Number of modified fields (for Save button counter)
  signedOffElements?: Set<string>; // Set of element keys that are signed off (for CheckCheck icon display)
  canExport?: boolean;
  onExport?: (action: ExportAction) => Promise<void> | void;
  exportDisabledReason?: string;
  exportInProgressAction?: ExportInProgressAction;
  onSavingChange?: (saving: boolean) => void;
  onRetryScenarios?: () => void; // Callback to retry scenario fetch when it fails
}

export const EditorToolbar: React.FC<Props> = ({
  templateKey,
  fuelType: propFuelType,
  elementKey,
  variant,
  channel = 'Email',
  previewMode = 'desktop',
  disabled = false,
  onElementChange,
  onVariantChange,
  onPreviewModeChange,
  hoverInvestigateEnabled = false,
  onHoverInvestigateChange,
  onSave,
  onOpenCompare,
  onCancel,
  renderOnlyUpperSection = false,
  renderOnlyLowerSection = false,
  availableElements,
  onRenderComplete,
  modifiedCount = 0,
  signedOffElements = new Set<string>(),
  canExport = false,
  onExport,
  exportDisabledReason,
  onSavingChange,
  exportInProgressAction = null,
  onRetryScenarios,
}) => {
  const { rc } = useGlobalConfig();
  const navigate = useNavigate();
  const level2Data = useSelector(selectLevel2Data);
  const [elements, setElements] = React.useState<{ elementKey: string; displayName: string }[]>([]);
  
  // Get data from Redux
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const supportedLocaleFromProject = useSelector(
    (state: RootState) => state.applicationScope.projectAttributes.supported_locale
  );

  // Pilot-supported fuel types (same source as Templates sidebar) for "all chips by default"
  const { availableFuelTypes: projectFuelTypes } = useAvailableFuelTypes(rc, selectedPilotId, true);

  // Fuel type for selected template (for scenarios/loading and display)
  const fuelType = React.useMemo(() => {
    const supported = projectFuelTypes.length > 0 ? projectFuelTypes : ['ELECTRIC'];

    // propFuelType is explicitly provided (even '' means "no fuel type" for templates like USER_WELCOME).
    // Use it directly rather than falling back to project default, so the Redux key used here
    // matches the key used by WysiwygPage when it stores the scenarios.
    if (propFuelType !== undefined) {
      if (propFuelType && supported.includes(propFuelType)) return propFuelType;
      // '' → no-fuel template; return '' so the Redux lookup key matches how scenarios were stored.
      return propFuelType;
    }

    // propFuelType not provided: extract from template key, then fall back to project default.
    if (templateKey) {
      const raw = getFuelTypeFromTemplateKey(templateKey);
      const normalized = raw ? mapMeasurementTypeToFuelType(raw) : undefined;
      if (normalized && supported.includes(normalized)) return normalized;
    }

    return supported[0];
  }, [templateKey, propFuelType, projectFuelTypes]);

  const locale = variant.locale || 'en_US';

  // Get error state from Redux
  const scenariosError = useSelector((state: RootState) =>
    selectedPilotId
      ? selectDataScenariosError(state, String(selectedPilotId), fuelType, locale)
      : null
  );

  // Format locale for display (e.g., "en_US" -> "English (US)")
  const localeDisplayText = React.useMemo(() => {
    const localeMap: Record<string, string> = {
      'en_US': 'English (US)',
      'es_US': 'Spanish (US)',
      'en_CA': 'English (CA)',
      'fr_CA': 'French (CA)',
    };
    return localeMap[locale] || locale;
  }, [locale]);

  // Get scenarios from Redux (full DataScenario objects)
  // Note: channel prop is now correctly set to 'HER' for HER templates by WysiwygPage
  const allScenarios = useSelector((state: RootState) =>
    templateKey && selectedPilotId
      ? selectScenariosForTemplate(
          state,
          String(selectedPilotId),
          fuelType,
          locale,
          templateKey, // Clean template key
          channel
        )
      : []
  );

  // Helper function to get technical section name from elementKey
  // Returns "FULL_TEMPLATE" for "Full Template", or technical section ID (e.g., "MONTHLY_SUMMARY_V2")
  const getTechnicalSectionName = React.useCallback((elementKey: string): string => {
    if (elementKey === 'Full Template') {
      return 'FULL_TEMPLATE';
    }
    
    // Check if elementKey is already a technical section ID (exists as key in mapping)
    if (SECTION_ID_TO_ELEMENT_KEY_MAP[elementKey]) {
      return elementKey;
    }
    
    // Reverse lookup: Find all technical section IDs that map to this display name
    // Prefer higher version numbers (e.g., V4 > V3 > V2)
    const matchingTechnicalIds: string[] = [];
    for (const [technicalId, displayName] of Object.entries(SECTION_ID_TO_ELEMENT_KEY_MAP)) {
      if (displayName === elementKey) {
        matchingTechnicalIds.push(technicalId);
      }
    }
    
    if (matchingTechnicalIds.length > 0) {
      // Sort by version number (higher first) and return the first one
      matchingTechnicalIds.sort((a, b) => {
        const versionA = parseInt(a.match(/_[Vv](\d+)/)?.[1] || '0');
        const versionB = parseInt(b.match(/_[Vv](\d+)/)?.[1] || '0');
        return versionB - versionA; // Higher version first
      });
      return matchingTechnicalIds[0];
    }
    
    // Fallback: if elementKey might be a technical section ID that's not in mapping, return as-is
    return elementKey;
  }, []);

  // Filter scenarios by templateName and elementNames
  // More lenient filtering: handles empty elementNames, Full Template case, and rendering failures
  // Also ensures template name from Template Sidebar is present in elementNames
  // Special handling for HER: filter by productType "HER" and FULL_TEMPLATE
  const scenarios = React.useMemo(() => {

    if (!templateKey) {
      return allScenarios;
    }

    // Special handling for HER templates
    // For HER, filter by productType "HER" and elementNames containing "FULL_TEMPLATE"
    const isHerTemplate = templateKey.startsWith('HER.');
    
    if (isHerTemplate) {
      // Match scenario fuel to the template's effective fuel (same as data-scenarios fetch / Redux key).
      // Do not use a fixed dot index: keys like HER.DFHER_MONTHLY_REPORT.ELECTRIC,GAS have the
      // composite token as the last segment; getFuelTypeFromTemplateKey returns undefined for that,
      // so we fall back to `fuelType` (sidebar / WysiwygPage), e.g. ELECTRIC.
      const herFuelType = (
        getFuelTypeFromTemplateKey(templateKey) ||
        fuelType ||
        ''
      )
        .trim()
        .toUpperCase();
      
      return allScenarios.filter((scenario: DataScenario) => {
        // Check productType is HER (case-insensitive)
        const scenarioProductType = (scenario.productType || '').trim().toUpperCase();
        if (scenarioProductType !== 'HER') {
          return false;
        }
        
        // Filter by fuelType
        const scenarioFuelType = (scenario.fuelType || '').trim().toUpperCase();
        if (scenarioFuelType && herFuelType && scenarioFuelType !== herFuelType) {
          return false;
        }
        
        // Filter by locale
        const scenarioLocale = (scenario.scenarioLocale || '').trim().toLowerCase();
        const selectedLocale = (locale || '').trim().toLowerCase();
        if (scenarioLocale && selectedLocale && scenarioLocale !== selectedLocale) {
          return false;
        }
        
        // Check elementNames contains FULL_TEMPLATE (for Full Template view)
        // or the specific section elementKey
        const elementNames = scenario.elementNames || [];
        if (elementNames.length === 0) {
          return false;
        }
        
        // For Full Template, require FULL_TEMPLATE in elementNames
        if (elementKey === 'Full Template') {
          return elementNames.includes('FULL_TEMPLATE');
        }
        
        // For specific sections, check if section is in elementNames
        return elementNames.includes(elementKey);
      });
    }

    // If elementKey is not available (e.g., rendering failed), show scenarios for the template
    // (section filtering can't be applied in this state)
    if (!elementKey) {
      return allScenarios.filter((scenario: DataScenario) => {
        // Check templateName match
        if (scenario.templateName !== templateKey) {
          return false;
        }

        // Filter by fuelType (if present on scenario)
        const scenarioFuelType = (scenario.fuelType || '').trim().toUpperCase();
        const selectedFuelType = (fuelType || '').trim().toUpperCase();
        if (scenarioFuelType && selectedFuelType && scenarioFuelType !== selectedFuelType) {
          return false;
        }

        // Filter by locale (if present on scenario)
        const scenarioLocale = (scenario.scenarioLocale || '').trim().toLowerCase();
        const selectedLocale = (locale || '').trim().toLowerCase();
        if (scenarioLocale && selectedLocale && scenarioLocale !== selectedLocale) {
          return false;
        }

        // Require elementNames to be non-empty
        const elementNames = scenario.elementNames || [];
        if (elementNames.length === 0) {
          return false; // Don't show scenarios with empty elementNames
        }

        return true;
      });
    }

    // Get technical section name for current elementKey
    const technicalSectionName = getTechnicalSectionName(elementKey);

    const __isWeb = (channel || '').toLowerCase() === 'web';
    // For Web Full Template, use clean template key
    const __sectionKeyForMatch = __isWeb && elementKey === 'Full Template' ? templateKey : technicalSectionName;

    // Filter scenarios with lenient matching:
    // 1. templateName must match templateKey
    // 2. elementNames array must include the technical section name OR be empty (empty means applies to all elements)
    // 3. template name from Template Sidebar should be present in elementNames
    const filtered = allScenarios.filter((scenario: DataScenario) => {
      // Check templateName match
      const templateNameMatch = scenario.templateName === templateKey;
      if (!templateNameMatch) {
        return false;
      }

      // Filter by fuelType
      // Normalize both sides to uppercase strings for stable comparison.
      const scenarioFuelType = (scenario.fuelType || '').trim().toUpperCase();
      const selectedFuelType = (fuelType || '').trim().toUpperCase();
      if (scenarioFuelType && selectedFuelType && scenarioFuelType !== selectedFuelType) {
        return false;
      }

      // Filter by locale
      const scenarioLocale = (scenario.scenarioLocale || '').trim().toLowerCase();
      const selectedLocale = (locale || '').trim().toLowerCase();
      if (scenarioLocale && selectedLocale && scenarioLocale !== selectedLocale) {
        return false;
      }
      
      // Check elementNames array
      const elementNames = scenario.elementNames || [];
      
      // Require elementNames to be non-empty - don't show scenarios with empty elementNames
      // The template name from Template Sidebar must be present in elementNames
      if (elementNames.length === 0) {
        return false;
      }
      
      // Check if elementNames includes the technical section name
      const hasSection = elementNames.includes(__sectionKeyForMatch);
      
      // Special case: if elementKey is "Full Template", also check for "FULL_TEMPLATE" in elementNames
      if (elementKey === 'Full Template' && !hasSection) {
        return elementNames.includes('FULL_TEMPLATE');
      }
      
      // If section is not found, don't show this scenario
      if (!hasSection) {
        return false;
      }

      // We intentionally do NOT require the base template name to appear in elementNames.
      // Filter is strictly based on:
      // 1) scenario.templateName === templateKey
      // 2) technical section name present in elementNames
      // 3) fuelType match
      // 4) locale match
      return true;
    });

    return filtered;
  }, [allScenarios, templateKey, elementKey, getTechnicalSectionName, fuelType, locale, channel]);

  // If the current variant.scenario doesn't match any available scenarioId (e.g. it's "default"),
  // align it to the default scenario (if any), otherwise the first scenario in the list,
  // so UI + render API stay consistent.
  React.useEffect(() => {
    if (!scenarios || scenarios.length === 0) return;

    const currentValue = variant.scenario || '';
    const hasMatchingValue = scenarios.some(ds => ds.scenarioId === currentValue);
    if (hasMatchingValue) return;

    const defaultScenarioId = scenarios.find(ds => ds.defaultScenario === true)?.scenarioId || '';
    const fallbackScenarioId = defaultScenarioId || scenarios[0]?.scenarioId || '';
    if (!fallbackScenarioId) return;
    if (currentValue === fallbackScenarioId) return;

    onVariantChange({ ...variant, scenario: fallbackScenarioId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarios, variant.scenario, templateKey, elementKey, channel, locale]);

  // Get loading state from Redux
  const scenariosLoading = useSelector((state: RootState) =>
    selectedPilotId
      ? selectDataScenariosLoading(state, String(selectedPilotId), fuelType, locale)
      : false
  );
  
  const [saving, setSaving] = React.useState(false);
  const [clusters, setClusters] = React.useState<Array<{ reference: string; name: string; id: string }>>([]);
  const [locales, setLocales] = React.useState<string[]>(DEFAULT_LOCALES);
  const [hierarchyMenuOpen, setHierarchyMenuOpen] = React.useState(false);
  const [hierarchyAnchorEl, setHierarchyAnchorEl] = React.useState<null | HTMLElement>(null);
  const [exportMenuAnchor, setExportMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [clusterMenuOpen, setClusterMenuOpen] = React.useState(false);
  const navToAudit = () => {
    const projectId = String(selectedPilotId || '');
    navigate(`${ROUTE_TREE.PROJECT.AUDIT_TRAIL.path}?projectId=${projectId}&source=cx_editor`, {
      state: { title: ROUTE_TREE.PROJECT.AUDIT_TRAIL.name },
    });
  };
  const [clustersLoading, setClustersLoading] = React.useState(false);
  const exportBusy = exportInProgressAction !== null;
  const exportTooltipTitle = exportBusy
    ? exportInProgressAction === 'clipboard'
      ? 'Copying image to clipboard...'
      : 'Preparing PNG download...'
    : canExport
    ? 'Export preview as PNG'
    : (exportDisabledReason ?? 'Export not available');

  // Transform Level 2 data to elements for the dropdown
  // Filter by productType (templateKey) and resolution === "ACCESS"
  // Special handling for HER: extract sections from featureValueResolutionData
  const level2Elements = React.useMemo(() => {
    if (!level2Data || !templateKey) {
      return [];
    }

    // Special handling for HER templates: extract sections from featureValueResolutionData
    if (templateKey.startsWith('HER.')) {
      // Find HER resolutions matching this templateKey (featureId)
      // Also look across all clusterReferences for this template
      const herResolutions = level2Data.filter(resolution => 
        resolution.featureId === templateKey && 
        resolution.resolution === 'ACCESS' &&
        resolution.product?.toUpperCase() === 'HER'
      );
      
      if (herResolutions.length > 0) {
        // Collect all unique sections from featureValueResolutionData across all cluster references
        const allSections = new Set<string>();
        
        herResolutions.forEach(resolution => {
          const rawData = resolution.featureValueResolutionData;
          if (rawData) {
            try {
              // Parse the JSON string: [["HEADER", "SHC_GRAPH_AND_WIDGET", ...], [...]]
              const fallbacks: string[][] = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
              
              if (Array.isArray(fallbacks)) {
                // Extract all unique sections from all fallback arrays
                fallbacks.forEach(fallback => {
                  if (Array.isArray(fallback)) {
                    fallback.forEach(section => {
                      if (section && typeof section === 'string') {
                        allSections.add(section);
                      }
                    });
                  }
                });
              }
            } catch (e) {
              console.error('[EditorToolbar] Failed to parse HER featureValueResolutionData:', e);
            }
          }
        });
        
        // Convert sections to element format
        const herElements = Array.from(allSections).map(sectionName => {
          // Try to get display name from mapping first
          let displayName = SECTION_ID_TO_ELEMENT_KEY_MAP[sectionName];
          
          // Fallback to simple transformation if no mapping found
          if (!displayName) {
            displayName = sectionName
              .split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(' ');
          }
          
          return {
            elementKey: sectionName,
            displayName: displayName
          };
        });
        
        // Sort alphabetically by display name
        return herElements.sort((a, b) => a.displayName.localeCompare(b.displayName));
      }
      
      return [];
    }

    // Special handling for Web templates: show all widgets from mapping + any additional from Level 2
    if (templateKey.startsWith('DASHBOARD.')) {
      // Remove fuel type suffix if present (e.g., "DASHBOARD.HOME.BILL_PROJECTION__ELECTRIC" -> "DASHBOARD.HOME.BILL_PROJECTION")
      const baseTemplateKey = templateKey.split('__')[0];

      // Check if this template has a widget mapping
      const widgetId = getWidgetIdFromTemplate(baseTemplateKey);

      if (widgetId) {
        // Check if this is a multi-widget template
        if (Array.isArray(widgetId)) {
          // Multi-widget template - only show "Full Template" option
          // Individual widgets cannot be edited separately
          return [];
        }

        // This is a single widget template - return it as the only section
        const displayName = getWebWidgetDisplayName(baseTemplateKey);
        return [{
          elementKey: baseTemplateKey,
          displayName: displayName
        }];
      }

      // For page-level templates (e.g., "DASHBOARD.HOME"), collect all widgets from Level 2 data
      // Extract the page name from the template key
      const parts = baseTemplateKey.split('.');
      const lastPart = parts[parts.length - 1]?.toUpperCase() || '';
      const hasFuelSuffix = VALID_FUEL_TYPES.includes(lastPart);
      const templateKeyWithoutFuel = hasFuelSuffix ? parts.slice(0, -1).join('.') : baseTemplateKey;
      const fuelTypeFromKey = hasFuelSuffix ? lastPart : null;

      // Collect sections from Level 2 data
      const sectionsFromL2 = level2Data
        .filter(resolution => {
          // Match productType (with fuel suffix stripped if present)
          if (resolution.productType !== templateKeyWithoutFuel) {
            return false;
          }

          // Must be ACCESS resolution with a section
          if (resolution.resolution !== 'ACCESS' || !resolution.section) {
            return false;
          }

          // If templateKey has fuel suffix, also filter by fuelType field
          if (fuelTypeFromKey && resolution.fuelType) {
            const resolutionFuelType = resolution.fuelType.toUpperCase();
            // Match exact fuel type
            return resolutionFuelType === fuelTypeFromKey;
          }

          // If no fuel suffix in templateKey, include all fuel types
          return true;
        })
        .map(resolution => ({
          section: resolution.section || '',
          position: resolution.position,
          displayName: resolution.displayName
        }));

      // If we found exactly one section from Level 2 and it matches the page name,
      // this is a standalone page template (e.g., DASHBOARD.SURVEY.GAS -> SURVEY)
      // Return it as a single editable section
      if (sectionsFromL2.length === 1) {
        const singleSection = sectionsFromL2[0];
        const sectionName = singleSection.section;

        // Try to get display name from mapping first
        let displayName = SECTION_ID_TO_ELEMENT_KEY_MAP[sectionName];

        // If no mapping, use the displayName from Level 2
        if (!displayName && singleSection.displayName) {
          displayName = singleSection.displayName;
        }

        // Fallback to simple transformation if no mapping found
        if (!displayName) {
          displayName = sectionName
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
        }

        return [{
          elementKey: sectionName,
          displayName: displayName
        }];
      }

      // Also add all widgets from WEB_TEMPLATE_TO_WIDGET_MAP that match this page
      // For example, if templateKey is "DASHBOARD.HOME", include all "DASHBOARD.HOME.*" widgets
      const allWidgetKeys = Object.keys(WEB_TEMPLATE_TO_WIDGET_MAP);
      const matchingWidgets = allWidgetKeys.filter(key => key.startsWith(templateKeyWithoutFuel + '.'));

      // Combine sections from Level 2 and widget mapping
      const allSections = new Set<string>();

      // Add sections from Level 2
      sectionsFromL2.forEach(({ section }) => {
        if (section) allSections.add(section);
      });

      // Add widgets from mapping (extract the last part as the section name)
      matchingWidgets.forEach(widgetKey => {
        const widgetParts = widgetKey.split('.');
        const sectionName = widgetParts[widgetParts.length - 1];
        if (sectionName) allSections.add(sectionName);
      });

      // Convert to element format
      const elements = Array.from(allSections).map(sectionName => {
        // Try to get display name from mapping first
        let displayName = SECTION_ID_TO_ELEMENT_KEY_MAP[sectionName];

        // If no exact match, try removing version suffix (e.g., _V2, _V3, _V4)
        if (!displayName) {
          const baseId = sectionName.replace(/_[Vv]\d+$/, '');
          displayName = SECTION_ID_TO_ELEMENT_KEY_MAP[baseId];
        }

        // If still no match, try removing all version suffixes
        if (!displayName) {
          const cleanId = sectionName.replace(/_[Vv]\d+.*$/, '');
          displayName = SECTION_ID_TO_ELEMENT_KEY_MAP[cleanId];
        }

        // Fallback to simple transformation if no mapping found
        if (!displayName) {
          displayName = sectionName
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
        }

        return {
          elementKey: sectionName,
          displayName: displayName,
          position: sectionsFromL2.find(s => s.section === sectionName)?.position
        };
      });

      // Sort by position if available, otherwise alphabetically
      return elements.sort((a, b) => {
        if (a.position !== null && a.position !== undefined && b.position !== null && b.position !== undefined) {
          return a.position - b.position;
        }
        if (a.position !== null && a.position !== undefined) return -1;
        if (b.position !== null && b.position !== undefined) return 1;
        return a.displayName.localeCompare(b.displayName);
      });
    }

    // Default handling for non-HER, non-Web templates (EMAIL, SMS)
    // Filter Level 2 data by productType matching templateKey and resolution === "ACCESS"
    // Handle fuel type suffix: if templateKey has fuel suffix (e.g., ".GAS", ".ELECTRIC"),
    // strip it for productType comparison but also filter by fuelType field
    const parts = templateKey.split('.');
    const lastPart = parts[parts.length - 1]?.toUpperCase() || '';
    const hasFuelSuffix = VALID_FUEL_TYPES.includes(lastPart);
    const templateKeyWithoutFuel = hasFuelSuffix ? parts.slice(0, -1).join('.') : templateKey;
    const fuelTypeFromKey = hasFuelSuffix ? lastPart : null;

    const filtered = level2Data
      .filter(resolution => {
        // Match productType (with fuel suffix stripped if present)
        if (resolution.productType !== templateKeyWithoutFuel) {
          return false;
        }

        // Must be ACCESS resolution with a section
        if (resolution.resolution !== 'ACCESS' || !resolution.section) {
          return false;
        }

        // If templateKey has fuel suffix, also filter by fuelType field
        if (fuelTypeFromKey && resolution.fuelType) {
          const resolutionFuelType = resolution.fuelType.toUpperCase();
          // Match exact fuel type
          return resolutionFuelType === fuelTypeFromKey;
        }

        // If no fuel suffix in templateKey, include all fuel types
        return true;
      })
      .sort((a, b) => {
        // Sort by position if available, otherwise by section name
        if (a.position !== null && b.position !== null) {
          return a.position - b.position;
        }
        if (a.position !== null) return -1;
        if (b.position !== null) return 1;
        return (a.section || '').localeCompare(b.section || '');
      })
      .map(resolution => {
        // Convert section name to display name using comprehensive mapping
        // Example: "HEADER_V4" -> "Header" (from mapping) or "Header V4" (fallback)
        const sectionName = resolution.section || '';

        // Try to get display name from mapping first
        let displayName = SECTION_ID_TO_ELEMENT_KEY_MAP[sectionName];

        // If no exact match, try removing version suffix (e.g., _V2, _V3, _V4)
        if (!displayName) {
          const baseId = sectionName.replace(/_[Vv]\d+$/, '');
          displayName = SECTION_ID_TO_ELEMENT_KEY_MAP[baseId];
        }

        // If still no match, try removing all version suffixes
        if (!displayName) {
          const cleanId = sectionName.replace(/_[Vv]\d+.*$/, '');
          displayName = SECTION_ID_TO_ELEMENT_KEY_MAP[cleanId];
        }

        // Fallback to simple transformation if no mapping found
        if (!displayName) {
          displayName = sectionName
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
        }

        return {
          elementKey: sectionName,
          displayName: displayName
        };
      });

    // Deduplicate by elementKey (sectionName) — level2Data may have multiple fuel-type entries per section
    const seen = new Set<string>();
    return filtered.filter(e => {
      if (seen.has(e.elementKey)) return false;
      seen.add(e.elementKey);
      return true;
    });
  }, [level2Data, templateKey]);

  // Update elements: prefer Level 2 data, fallback to availableElements from props
  React.useEffect(() => {
    if (level2Elements.length > 0) {
      // Use Level 2 data with "Full Template" as first option
      setElements([
        { elementKey: 'Full Template', displayName: 'Full Template' },
        ...level2Elements
      ]);
    } else if (availableElements && availableElements.length > 0) {
      // Fallback to availableElements from props (parsed from HTML)
      setElements(availableElements);
    } else {
      // Default fallback
      setElements([{ elementKey: 'Full Template', displayName: 'Full Template' }]);
    }
  }, [level2Elements, availableElements]);
  
  // EditorToolbar no longer fetches template meta - it's done in WysiwygPage to prevent duplicates
  // Scenarios are passed via props/callback from WysiwygPage

  // Load CONTENT clusters: GET /v1/get-clusters/<projectId>?cluster_categories=CONTENT (called on mount and every time Clusters dropdown is opened)
  const loadClusters = React.useCallback(async () => {
    if (!selectedPilotId) {
      setClusters([]);
      setClustersLoading(false);
      return;
    }
    setClustersLoading(true);
    try {
      const resp = await rc.bffClient.getClustersList(String(selectedPilotId), 'CONTENT');
      const raw = (resp as any)?.data;
      const list = raw?.data ?? (Array.isArray(raw) ? raw : []);
      const items = Array.isArray(list)
        ? list.map((c: any) => ({
            reference: c.cluster_reference ?? c.cluster_name ?? c.name ?? String(c),
            name: c.cluster_name ?? c.name ?? c.cluster_reference ?? String(c),
            id: c.cluster_id ?? c.clusterId ?? ''
          }))
        : [];
      setClusters(items);
    } catch {
      setClusters([]);
    } finally {
      setClustersLoading(false);
    }
  }, [selectedPilotId, rc]);

  React.useEffect(() => {
    loadClusters();
  }, [loadClusters]);

  // Normalize project supported_locale into a locale list.
  const normalizedSupportedLocales = React.useMemo((): string[] => {
    const raw = supportedLocaleFromProject;
    if (!raw) return DEFAULT_LOCALES;

    const normalize = (arr: any[]): string[] => {
      const unique = Array.from(
        new Set(
          arr
            .map((v) => normalizeLocaleCode(String(v || '').trim()))
            .filter(Boolean)
        )
      );
      return unique.length > 0 ? unique : DEFAULT_LOCALES;
    };

    if (Array.isArray(raw)) {
      return normalize(raw);
    }

    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      if (!trimmed) return DEFAULT_LOCALES;

      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return normalize(parsed);
      } catch {
        // Continue with CSV parsing below.
      }

      // Handle comma-separated values (e.g., "en_US,es_US").
      return normalize(trimmed.split(','));
    }

    return DEFAULT_LOCALES;
  }, [supportedLocaleFromProject]);

  // Load supported locales from project attributes.
  React.useEffect(() => {
    setLocales(normalizedSupportedLocales);
    const normalizedCurrent = normalizeLocaleCode(variant.locale || '');
    const matchedLocale = normalizedSupportedLocales.find(
      (l) => normalizeLocaleCode(l) === normalizedCurrent
    );
    if (matchedLocale && matchedLocale !== variant.locale) {
      onVariantChange({ ...variant, locale: matchedLocale });
      return;
    }
    if (!matchedLocale) {
      onVariantChange({ ...variant, locale: normalizedSupportedLocales[0] });
    }
  }, [normalizedSupportedLocales, variant.locale, onVariantChange]);

  // Get template name and channel from Redux Level 1 or Level 2 data
  const level1Data = useSelector(selectLevel1Data);
  const { templateName, templateChannel, templateFuelType } = React.useMemo(() => {
    if (!templateKey) return { templateName: '', templateChannel: channel, templateFuelType: undefined };

    // For Web templates with fuel suffix (e.g., "DASHBOARD.HOME.BILL_HISTORY__GAS"),
    // extract the fuel type and strip it from the template key for display
    let cleanTemplateKey = templateKey;
    let extractedFuelType: string | undefined = undefined;

    if (templateKey.includes('__')) {
      const parts = templateKey.split('__');
      cleanTemplateKey = parts[0]; // Base template key without suffix
      extractedFuelType = parts[1]; // Fuel type (e.g., "GAS", "ELECTRIC", "WATER")
    }

    // Check if this is an HER template (templateKey starts with "HER.")
    // HER templates come from Level 2 data with format "HER.HER_MONTHLY_REPORT.ELECTRIC"
    if (cleanTemplateKey.startsWith('HER.')) {
      const parts = cleanTemplateKey.split('.');
      if (parts.length >= 2) {
        if (isHerDfherMonthlyReportTemplateKey(cleanTemplateKey)) {
          return {
            templateName: HER_DFHER_MONTHLY_REPORT_DISPLAY_NAME,
            templateChannel: 'HER',
            templateFuelType: extractedFuelType
          };
        }
        // Extract template name from second part: "HER_MONTHLY_REPORT" -> "Her Monthly Report"
        const herTemplateName = parts[1];
        const displayName = herTemplateName
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
        return { templateName: displayName, templateChannel: 'HER', templateFuelType: extractedFuelType };
      }
    }

    // Try to find template in Level 1 data using the clean template key
    if (level1Data) {
      const templateResolution = level1Data.find(r => r.featureId === cleanTemplateKey);
      if (templateResolution) {
        // Extract display name from featureId; avoid using fuel type as name (e.g. "EMAIL.SF.BILL_PROJECTION.GAS" -> "Bill Projection", not "Gas")
        const parts = templateResolution.featureId.split('.');
        let namePart = parts[parts.length - 1] || templateResolution.featureId;
        if (parts.length >= 2 && VALID_FUEL_TYPES.includes(namePart.toUpperCase())) {
          namePart = parts[parts.length - 2] || namePart;
        }
        const displayName = namePart
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');

        // Map product to channel
        const product = templateResolution.product.toUpperCase();
        let channelName = channel;
        if (product === 'EMAIL') channelName = 'Email';
        else if (product === 'HER') channelName = 'HER';
        else if (product === 'WEB') channelName = 'Web';
        else if (product === 'SMS') channelName = 'SMS';

        return { templateName: displayName, templateChannel: channelName, templateFuelType: extractedFuelType };
      }
    }

    // Fallback: use clean templateKey as name
    return { templateName: cleanTemplateKey, templateChannel: channel, templateFuelType: extractedFuelType };
  }, [templateKey, channel, level1Data]);

  // Extract prefix from template name, or derive from templateKey for HER templates
  const prefixMatch = templateName.match(/\(([^)]+)\)/);
  let prefix = prefixMatch ? prefixMatch[0] : '';
  
  // For HER templates, extract fuel type badge from templateKey
  // HER.HER_MONTHLY_REPORT.ELECTRIC -> "EL", HER.HER_MONTHLY_REPORT.GAS -> "GAS"
  if (!prefix && templateChannel === 'HER' && templateKey) {
    const parts = templateKey.split('.');
    const lastPart = parts[parts.length - 1] || '';
    
    // Check if it contains comma (multiple fuel types)
    if (lastPart.includes(',')) {
      prefix = '(DF)'; // Dual Fuel
    } else {
      // Map single fuel types to badge labels
      const fuelType = lastPart.toUpperCase();
      if (fuelType === 'ELECTRIC') {
        prefix = '(EL)';
      } else if (fuelType === 'GAS') {
        prefix = '(GAS)';
      } else if (fuelType === 'WATER') {
        prefix = '(WTR)';
      } else if (fuelType.length > 0) {
        // Fallback: use first 3 characters
        prefix = `(${fuelType.substring(0, 3)})`;
      }
    }
  }
  
  // If no prefix in template name but it's an Email template, default to EL
  if (!prefix && templateChannel === 'Email') {
    prefix = '(EL)';
  }
  const nameWithoutPrefix = templateName.replace(/\(([^)]+)\)/, '').trim();
  const displayPrefix = prefix.replace(/[()]/g, '');
  // Avoid repeating fuel name after the chip (e.g. "Gas" when we already show GAS chip)
  const fuelFullNames = Object.values(FUEL_TYPE_FULL_NAME);
  const displayTitle = (nameWithoutPrefix && !fuelFullNames.includes(nameWithoutPrefix.trim()))
    ? nameWithoutPrefix
    : (templateKey || 'Select a template');

  const hierarchyValue = variant.hierarchy.startsWith('Cluster:') 
    ? variant.hierarchy.replace('Cluster:', '') 
    : variant.hierarchy;
  const hierarchyDisplayText = hierarchyValue === 'Project' ? 'Project' : `Cluster: ${hierarchyValue}`;
  const selectedScenarioLabel = scenarios.find(ds => ds.scenarioId === variant.scenario)?.scenarioDisplayName || variant.scenario;

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const handleHierarchyClick = (event: React.MouseEvent<HTMLElement>) => {
    setHierarchyAnchorEl(event.currentTarget);
    setHierarchyMenuOpen(true);
  };

  const handleHierarchyClose = () => {
    setHierarchyMenuOpen(false);
    setClusterMenuOpen(false);
    setHierarchyAnchorEl(null);
  };

  const handleProjectSelect = () => {
    onVariantChange({ ...variant, hierarchy: 'Project', clusterId: undefined });
    handleHierarchyClose();
  };

  const handleClusterSelect = (clusterReference: string, clusterId: string) => {
    onVariantChange({ ...variant, hierarchy: `Cluster:${clusterReference}`, clusterId: clusterId || undefined });
    handleHierarchyClose();
  };

  // When channel is not Email, cluster is not available; reset hierarchy to Project if currently a cluster
  React.useEffect(() => {
    if (templateChannel !== 'Email' && variant.hierarchy?.startsWith('Cluster:')) {
      onVariantChange({ ...variant, hierarchy: 'Project', clusterId: undefined });
    }
  }, [templateChannel]); // eslint-disable-line react-hooks/exhaustive-deps -- only run when channel changes

  const elementDropdownTitle = React.useMemo(() => {
    switch (templateChannel) {
      case 'HER': return 'HER Modules';
      case 'Web': return 'Web Widgets';
      case 'Email':
      default:
        return 'Email Elements';
    }
  }, [templateChannel]);

  // If only rendering upper section, return just that
  if (renderOnlyUpperSection) {
    return (
      <>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1, // gap-2 in Tailwind = 8px = gap: 1 in MUI
          border: theme => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 1, // p-2 in Tailwind = 8px = p: 1 in MUI
          bgcolor: 'background.paper',
          width: '100%'
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleHierarchyClick}
            endIcon={<ChevronDown size={16} />}
            startIcon={<Network size={16} />}
            sx={{
              height: 32, // h-8 = 32px
              minWidth: 180, // w-[180px]
              fontSize: '0.875rem',
              textTransform: 'none',
              justifyContent: 'space-between',
              '& .MuiButton-startIcon': {
                marginRight: '8px',
                color: 'text.secondary'
              },
              '& .MuiButton-endIcon': {
                marginLeft: 'auto',
                opacity: 0.5
              }
            }}
          >
            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {truncateText(hierarchyDisplayText, 20)}
            </Typography>
          </Button>
          <Menu
            anchorEl={hierarchyAnchorEl}
            open={hierarchyMenuOpen}
            onClose={handleHierarchyClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{
              sx: {
                maxHeight: 400
              }
            }}
            MenuListProps={{
              sx: {
                minWidth: 200,
                py: 0.5,
                maxHeight: 400,
                overflow: 'auto',
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1,
                  minHeight: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  fontSize: '0.875rem'
                }
              }
            }}
          >
            <MenuItem 
              onClick={handleProjectSelect} 
              selected={hierarchyValue === 'Project'}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                px: 2,
                py: 1,
                minHeight: 40,
                fontSize: '0.875rem'
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: '0.875rem',
                  textAlign: 'left',
                  margin: 0,
                  padding: 0,
                  width: '100%'
                }}
              >
                Project
              </Typography>
            </MenuItem>
            {templateChannel === 'Email' && [
              <Divider key="cluster-divider" />,
              <MenuItem
                key="cluster-item"
                onClick={() => {
                    loadClusters();
                    setClusterMenuOpen(prev => !prev);
                  }}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 2,
                    py: 1,
                    minHeight: 40,
                    fontSize: '0.875rem'
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.875rem',
                      textAlign: 'left',
                      margin: 0,
                      padding: 0,
                      flex: 1
                    }}
                  >
                    Clusters
                  </Typography>
                  <ChevronDown size={16} style={{ marginLeft: '8px', opacity: 0.7 }} />
              </MenuItem>,
              clusterMenuOpen && (
                  <Menu
                    key="cluster-submenu"
                    open={clusterMenuOpen}
                    anchorEl={hierarchyAnchorEl}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    onClose={() => setClusterMenuOpen(false)}
                    PaperProps={{
                      sx: {
                        maxHeight: 400,
                        mt: 0.5,
                        ml: 0.5,
                        boxShadow: 2
                      }
                    }}
                    MenuListProps={{
                      sx: {
                        minWidth: 200,
                        py: 0.5,
                        maxHeight: 400,
                        overflow: 'auto',
                        '& .MuiMenuItem-root': {
                          px: 2,
                          py: 1,
                          minHeight: 40,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          fontSize: '0.875rem'
                        }
                      }
                    }}
                  >
                    {clustersLoading ? (
                      <MenuItem disabled>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', textAlign: 'left' }}>
                          Loading…
                        </Typography>
                      </MenuItem>
                    ) : clusters.length === 0 ? (
                      <MenuItem disabled sx={{ opacity: 0.8 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', textAlign: 'left' }}>
                          No content cluster is present.
                        </Typography>
                      </MenuItem>
                    ) : (
                      clusters.map(c => (
                        <Tooltip key={c.reference} title={c.name} placement="left">
                          <MenuItem
                            onClick={() => handleClusterSelect(c.reference, c.id)}
                            selected={hierarchyValue === c.reference}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          px: 2,
                          py: 1,
                          minHeight: 40,
                          fontSize: '0.875rem'
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.875rem',
                            textAlign: 'left',
                            margin: 0,
                            padding: 0,
                            width: '100%'
                          }}
                        >
                          {c.reference}
                        </Typography>
                      </MenuItem>
                    </Tooltip>
                  ))
                )}
              </Menu>
              ),
            ]}
          </Menu>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={variant.locale}
                onChange={(e) => onVariantChange({ ...variant, locale: e.target.value as string })}
                displayEmpty
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  '& .MuiSelect-select': {
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '0.875rem'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 400,
                      zIndex: 1300, // Ensure menu appears above other elements
                      '& .MuiMenuItem-root': {
                        fontSize: '0.875rem'
                      }
                    }
                  },
                  MenuListProps: {
                    sx: {
                      maxHeight: 400,
                      overflow: 'auto'
                    }
                  },
                  disableAutoFocus: false,
                  disableEnforceFocus: false,
                  disableRestoreFocus: false
                }}
                renderValue={(value) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Languages size={16} style={{ color: 'inherit', opacity: 0.7 }} />
                    <span style={{ fontSize: '0.875rem' }}>{value}</span>
                  </Box>
                )}
              >
                {locales.map(l => (
                  <MenuItem key={l} value={l} sx={{ fontSize: '0.875rem' }}>{l}</MenuItem>
                ))}
              </Select>
            </FormControl>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Tooltip title="This feature is coming soon">
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {}} // No-op: functionality preserved but disabled
                disabled={true}
                startIcon={<GitCompareArrows size={16} />}
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }}
              >
                Compare
              </Button>
            </span>
          </Tooltip>

          <Tooltip title="View Audit Trail">
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={navToAudit}
                startIcon={<FileClock size={16} />}
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                }}
              >
                Audit
              </Button>
            </span>
          </Tooltip>

          <Tooltip title="This feature is coming soon">
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {}} // No-op: functionality preserved but disabled
                disabled={true}
                startIcon={<ClipboardCheck size={16} />}
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }}
              >
                Checklist
              </Button>
            </span>
          </Tooltip>

          {onCancel && (
            <Button
              variant="text"
              size="small"
              onClick={onCancel}
              disabled={modifiedCount === 0}
              sx={{
                height: 32,
                fontSize: '0.875rem',
                textTransform: 'none',
                color: 'error.main',
                ...(modifiedCount === 0 && { opacity: 0.5 })
              }}
            >
              Cancel
            </Button>
          )}

          <PermissionGate require={CAPABILITIES.WYSIWYG_SAVE} mode="disable">
            <Button
              variant="contained"
              size="small"
              onClick={async () => {
                if (!onSave) return;
                try { setSaving(true); onSavingChange?.(true); await onSave(); } finally { setSaving(false); onSavingChange?.(false); }
              }}
              disabled={!templateKey || saving || modifiedCount === 0}
              startIcon={<Save size={16} />}
              sx={(theme) => ({
                height: 32,
                fontSize: '0.875rem',
                textTransform: 'none',
                minWidth: 100,
                ...(modifiedCount === 0 && {
                  bgcolor: alpha(theme.palette.primary.main, 0.3), // Bluish background with reduced opacity
                  color: alpha(theme.palette.primary.main, 0.5), // Bluish text color
                  opacity: 0.6,
                  cursor: 'not-allowed',
                  filter: 'blur(0.5px)',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.3), // Prevent hover effect
                  }
                })
              })}
            >
              {saving ? 'Saving…' : `Save${modifiedCount > 0 ? ` (${modifiedCount})` : ''}`}
            </Button>
          </PermissionGate>
        </Box>
      </Box>
      </>
    );
  }

  // If only rendering lower section, return just that
  if (renderOnlyLowerSection) {
    return (
      <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1, // gap-2 in Tailwind = 8px = gap: 1 in MUI
          border: theme => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 1, // p-2 in Tailwind = 8px = p: 1 in MUI
          bgcolor: 'background.paper',
          flex: 1,
          minWidth: 0
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={templateChannel}
              size="small"
              color="primary"
              sx={{
                fontWeight: 600,
                bgcolor: 'primary.main',
                color: 'primary.contrastText'
              }}
            />
            {/* Display fuel chip for all channels (Email, HER, Web) */}
            {((templateChannel !== 'Web' && fuelType) || (templateChannel === 'Web' && templateFuelType)) && (
              <Tooltip
                title={
                  templateChannel === 'Web'
                    ? FUEL_TYPE_FULL_NAME[(templateFuelType || fuelType) || ''] ??
                      (templateFuelType || fuelType)
                    : isHerDfherMonthlyReportTemplateKey(templateKey)
                      ? 'Dual Fuel'
                      : FUEL_TYPE_FULL_NAME[fuelType] ?? fuelType
                }
              >
                <Chip
                  label={
                    templateChannel === 'Web'
                      ? getFuelTypeLabel(templateFuelType || fuelType)
                      : getHerFuelChipLabel(templateKey, fuelType)
                  }
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.75rem',
                    height: 20,
                    width: 70,
                    px: 0.5,
                    py: 0.5,
                    cursor: 'default',
                    borderColor: theme => alpha(theme.palette.primary.main, 0.5),
                    color: 'primary.main',
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                    '& .MuiChip-label': {
                      textAlign: 'center',
                      width: '100%',
                      px: 0
                    }
                  }}
                />
              </Tooltip>
            )}
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
              {displayTitle}
            </Typography>
          </Box>
          {(onHoverInvestigateChange || (templateChannel !== 'HER' && onPreviewModeChange) || !!onExport) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Desktop/Mobile toggle: HER previews are desktop-only, so omit for HER. */}
              {templateChannel !== 'HER' && onPreviewModeChange && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    border: theme => `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    p: 0.5,
                    bgcolor: 'background.default'
                  }}
                >
                  <Button
                    variant={previewMode === 'desktop' ? 'contained' : 'text'}
                    size="small"
                    onClick={() => onPreviewModeChange('desktop')}
                    sx={{
                      minWidth: 28,
                      height: 28,
                      p: 0
                    }}
                  >
                    <Monitor size={16} />
                  </Button>
                  <Button
                    variant={previewMode === 'mobile' ? 'contained' : 'text'}
                    size="small"
                    onClick={() => onPreviewModeChange('mobile')}
                    sx={{
                      minWidth: 28,
                      height: 28,
                      p: 0
                    }}
                  >
                    <Smartphone size={16} />
                  </Button>
                </Box>
              )}
              {onHoverInvestigateChange && (channel === 'HER' || channel === 'Web' ? (
                <Tooltip title="This feature is coming soon">
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      aria-label="Toggle hover investigate"
                      onClick={() => {}} // No-op: functionality preserved but disabled
                      disabled={true}
                      sx={{
                        minWidth: 32,
                        width: 32,
                        height: 32,
                        p: 0,
                        opacity: 0.5,
                        cursor: 'not-allowed'
                      }}
                    >
                      <Pipette size={16} />
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title="Hover & Investigate">
                  <Button
                    variant={hoverInvestigateEnabled ? 'contained' : 'outlined'}
                    size="small"
                    aria-label="Toggle hover investigate"
                    onClick={() => onHoverInvestigateChange?.(!hoverInvestigateEnabled)}
                    sx={{
                      minWidth: 32,
                      width: 32,
                      height: 32,
                      p: 0
                    }}
                  >
                    <Pipette size={16} />
                  </Button>
                </Tooltip>
              ))}
              {onExport && (
                <>
                  <Tooltip title={exportTooltipTitle}>
                    <span>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!canExport || exportBusy}
                        onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                        sx={{ minWidth: 28, height: 28, p: 0 }}
                      >
                        {exportBusy ? <CircularProgress size={16} thickness={5} /> : <Download size={16} />}
                      </Button>
                    </span>
                  </Tooltip>
                  <Menu
                    anchorEl={exportMenuAnchor}
                    open={Boolean(exportMenuAnchor)}
                    onClose={() => setExportMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <MenuItem disabled={exportBusy} onClick={() => { setExportMenuAnchor(null); onExport('download'); }}>
                      <ListItemText primary="Download as PNG" />
                    </MenuItem>
                    <MenuItem disabled={exportBusy} onClick={() => { setExportMenuAnchor(null); onExport('clipboard'); }}>
                      <ListItemText primary="Copy to clipboard" />
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={hierarchyDisplayText} 
              size="small"
              variant="outlined"
            />
            <Chip 
              label={localeDisplayText} 
              size="small"
              variant="outlined"
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={elementKey}
                onChange={(e) => onElementChange(e.target.value as string)}
                disabled={disabled || !elements.length}
                displayEmpty
                  sx={{
                    height: 32,
                    fontSize: '0.875rem',
                    '& .MuiSelect-select': {
                      py: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: '0.875rem'
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 400,
                        zIndex: 1300, // Ensure menu appears above other elements
                        '& .MuiMenuItem-root': {
                          fontSize: '0.875rem'
                        }
                      }
                    },
                    MenuListProps: {
                      sx: {
                        maxHeight: 400,
                        overflow: 'auto'
                      }
                    },
                    disableAutoFocus: false,
                    disableEnforceFocus: false,
                    disableRestoreFocus: false
                  }}
                  renderValue={(value) => {
                    const selected = (elements.length ? elements : [{ elementKey: 'Full Template', displayName: 'Full Template' }])
                      .find(e => e.elementKey === value);
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Library size={16} style={{ color: 'inherit', opacity: 0.7 }} />
                        <span style={{ fontSize: '0.875rem' }}>{selected?.displayName || value}</span>
                      </Box>
                    );
                  }}
                >
                  {(elements.length ? elements : [{ elementKey: 'Full Template', displayName: 'Full Template' }]).map(e => (
                    <MenuItem 
                      key={e.elementKey} 
                      value={e.elementKey} 
                      sx={{ 
                        fontSize: '0.875rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      {e.elementKey === 'Full Template' && <Library size={16} style={{ marginRight: '8px' }} />}
                        <span>{e.displayName}</span>
                      </Box>
                      {signedOffElements.has(e.elementKey) && (
                        <CheckCheck size={16} style={{ color: '#16a34a', flexShrink: 0 }} />
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

            <FormControl
              size="small" 
              sx={{ minWidth: 180 }}
              error={!!scenariosError}
            >
              <Select
                value={(() => {
                  // Ensure the value matches one of the available scenario values
                  const currentValue = variant.scenario || '';
                  
                  // If no scenarios available, return empty string or current value
                  if (scenarios.length === 0) {
                    return currentValue || '';
                  }
                  
                  // Check if current value exists in available scenarios
                  const hasMatchingValue = scenarios.some(ds => ds.scenarioId === currentValue);
                  
                  if (!hasMatchingValue) {
                    // If current value doesn't match, try to find default scenario first
                    const defaultScenario = scenarios.find(ds => ds.defaultScenario === true);
                    if (defaultScenario) {
                      return defaultScenario.scenarioId;
                    }
                    // Otherwise use the first scenario
                    return scenarios[0]?.scenarioId || '';
                  }
                  
                  return currentValue;
                })()}
                onChange={(e) => {
                  // Store the scenarioId (e.g., "MS_1") in variant.scenario, which will be sent to API
                  // The scenarioDisplayName (e.g., "Default") is only for display in the UI
                  const selectedValue = e.target.value as string;
                  if (selectedValue) {
                    onVariantChange({ ...variant, scenario: selectedValue });
                  }
                }}
                displayEmpty
                disabled={disabled || scenariosLoading || scenarios.length === 0}
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  '& .MuiSelect-select': {
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '0.875rem'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 400,
                      zIndex: 1300, // Ensure menu appears above other elements
                      '& .MuiMenuItem-root': {
                        fontSize: '0.875rem'
                      }
                    }
                  },
                  MenuListProps: {
                    sx: {
                      maxHeight: 400,
                      overflow: 'auto'
                    }
                  },
                  disableAutoFocus: false,
                  disableEnforceFocus: false,
                  disableRestoreFocus: false
                }}
                renderValue={(value) => {
                  // Note: 'value' parameter is the scenarioId (e.g., "MS_1"), but we display the scenarioDisplayName (e.g., "Default")
                  if (scenariosLoading) {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={14} />
                        <span style={{ fontSize: '0.875rem', opacity: 0.7 }}>Loading...</span>
                      </Box>
                    );
                  }
                  const selected = scenarios.find(ds => ds.scenarioId === value);
                  if (!selected && scenarios.length === 0) {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Layers size={16} style={{ color: 'inherit', opacity: 0.5 }} />
                        <span style={{ fontSize: '0.875rem', opacity: 0.7 }}>
                          {scenariosError ? 'Error loading' : 'No scenarios'}
                        </span>
                      </Box>
                    );
                  }
                  // Display scenarioDisplayName in UI, but scenarioId (e.g., "MS_1") is stored in variant.scenario and sent to API
                  const displayName = selected?.scenarioDisplayName || selected?.scenarioId || value;
                  return (
                    <Tooltip title={truncateText(displayName, 14) !== displayName ? displayName : ''} placement="top">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Layers size={16} style={{ color: 'inherit', opacity: 0.7 }} />
                      <span style={{ fontSize: '0.875rem' }}>{truncateText(displayName, 14)}</span>
                    </Box>
                    </Tooltip>
                  );
                }}
              >
                {scenariosLoading ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <CircularProgress size={14} />
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>Loading scenarios...</Typography>
                    </Box>
                  </MenuItem>
                ) : scenarios.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                      {scenariosError ? 'Error loading scenarios' : 'No scenarios available'}
                    </Typography>
                  </MenuItem>
                ) : (
                  [
                    ...scenarios.map((ds, index) => (
                      <MenuItem 
                        key={`${ds.scenarioId}-${index}`}
                        value={ds.scenarioId} 
                        sx={{ 
                          fontSize: '0.875rem',
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap'
                        }}
                        title={truncateText(ds.scenarioDisplayName || ds.scenarioId, 14) !== (ds.scenarioDisplayName || ds.scenarioId) ? (ds.scenarioDisplayName || ds.scenarioId) : ''}
                      >
                        {ds.scenarioDisplayName || ds.scenarioId}
                      </MenuItem>
                    )),
                    <Divider key="divider" />,
                    <MenuItem 
                      key="manage-scenarios"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.location.href = '/content-management/data-scenarios';
                      }}
                      sx={{ 
                        fontSize: '0.875rem',
                        color: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        py: 1,
                        px: 2,
                        '&:hover': {
                          textDecoration: 'underline',
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <PlusCircle size={16} />
                      <span>Manage Scenarios...</span>
                    </MenuItem>
                  ]
                )}
              </Select>
              {scenariosError && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, ml: 1.75 }}>
                  <Typography variant="caption" sx={{ color: 'error.main' }}>
                    {scenariosError}
                  </Typography>
                  {onRetryScenarios && (
                    <Button size="small" variant="text" color="error" sx={{ minWidth: 0, px: 0.5, py: 0 }} onClick={onRetryScenarios}>
                      Retry
                    </Button>
                  )}
                </Box>
              )}
            </FormControl>
          </Box>
        </Box>
      </Box>
      </>
    );
  }

  // Default: render both sections
  return (
    <>
      {/* Top Toolbar Section - Full Width */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1, // gap-2 in Tailwind = 8px = gap: 1 in MUI
          border: theme => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 1, // p-2 in Tailwind = 8px = p: 1 in MUI
          bgcolor: 'background.paper',
          width: '100%'
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleHierarchyClick}
            endIcon={<ChevronDown size={16} />}
            startIcon={<Network size={16} />}
            sx={{
              height: 32,
              minWidth: 180,
              fontSize: '0.875rem',
              textTransform: 'none',
              justifyContent: 'space-between',
              '& .MuiButton-startIcon': {
                marginRight: '8px',
                color: 'text.secondary'
              },
              '& .MuiButton-endIcon': {
                marginLeft: 'auto',
                opacity: 0.5
              }
            }}
          >
            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {truncateText(hierarchyDisplayText, 20)}
            </Typography>
          </Button>
          <Menu
            anchorEl={hierarchyAnchorEl}
            open={hierarchyMenuOpen}
            onClose={handleHierarchyClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{
              sx: {
                maxHeight: 400
              }
            }}
            MenuListProps={{
              sx: {
                minWidth: 200,
                py: 0.5,
                maxHeight: 400,
                overflow: 'auto',
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1,
                  minHeight: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  fontSize: '0.875rem'
                }
              }
            }}
          >
            <MenuItem 
              onClick={handleProjectSelect} 
              selected={hierarchyValue === 'Project'}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                px: 2,
                py: 1,
                minHeight: 40,
                fontSize: '0.875rem'
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: '0.875rem',
                  textAlign: 'left',
                  margin: 0,
                  padding: 0,
                  width: '100%'
                }}
              >
                Project
              </Typography>
            </MenuItem>
            {templateChannel === 'Email' && [
              <Divider key="cluster-divider" />,
              <MenuItem
                key="cluster-item"
                onClick={() => {
                    loadClusters();
                    setClusterMenuOpen(prev => !prev);
                  }}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 2,
                    py: 1,
                    minHeight: 40,
                    fontSize: '0.875rem'
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.875rem',
                      textAlign: 'left',
                      margin: 0,
                      padding: 0,
                      flex: 1
                    }}
                  >
                    Clusters
                  </Typography>
                  <ChevronDown size={16} style={{ marginLeft: '8px', opacity: 0.7 }} />
              </MenuItem>,
              clusterMenuOpen && (
                  <Menu
                    key="cluster-submenu"
                    open={clusterMenuOpen}
                    anchorEl={hierarchyAnchorEl}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    onClose={() => setClusterMenuOpen(false)}
                    PaperProps={{
                      sx: {
                        maxHeight: 400,
                        mt: 0.5,
                        ml: 0.5,
                        boxShadow: 2
                      }
                    }}
                    MenuListProps={{
                      sx: {
                        minWidth: 200,
                        py: 0.5,
                        maxHeight: 400,
                        overflow: 'auto',
                        '& .MuiMenuItem-root': {
                          px: 2,
                          py: 1,
                          minHeight: 40,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          fontSize: '0.875rem'
                        }
                      }
                    }}
                  >
                    {clustersLoading ? (
                      <MenuItem disabled>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', textAlign: 'left' }}>
                          Loading…
                        </Typography>
                      </MenuItem>
                    ) : clusters.length === 0 ? (
                      <MenuItem disabled sx={{ opacity: 0.8 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', textAlign: 'left' }}>
                          No content cluster is present.
                        </Typography>
                      </MenuItem>
                    ) : (
                      clusters.map(c => (
                        <Tooltip key={c.reference} title={c.name} placement="left">
                          <MenuItem
                            onClick={() => handleClusterSelect(c.reference, c.id)}
                            selected={hierarchyValue === c.reference}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          px: 2,
                          py: 1,
                          minHeight: 40,
                          fontSize: '0.875rem'
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.875rem',
                            textAlign: 'left',
                            margin: 0,
                            padding: 0,
                            width: '100%'
                          }}
                        >
                          {c.reference}
                        </Typography>
                      </MenuItem>
                    </Tooltip>
                  ))
                )}
              </Menu>
              ),
            ]}
          </Menu>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={variant.locale}
                onChange={(e) => onVariantChange({ ...variant, locale: e.target.value as string })}
                displayEmpty
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  '& .MuiSelect-select': {
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '0.875rem'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 400,
                      zIndex: 1300, // Ensure menu appears above other elements
                      '& .MuiMenuItem-root': {
                        fontSize: '0.875rem'
                      }
                    }
                  },
                  MenuListProps: {
                    sx: {
                      maxHeight: 400,
                      overflow: 'auto'
                    }
                  },
                  disableAutoFocus: false,
                  disableEnforceFocus: false,
                  disableRestoreFocus: false
                }}
                renderValue={(value) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Languages size={16} style={{ color: 'inherit', opacity: 0.7 }} />
                    <span style={{ fontSize: '0.875rem' }}>{value}</span>
                  </Box>
                )}
              >
                {locales.map(l => (
                  <MenuItem key={l} value={l} sx={{ fontSize: '0.875rem' }}>{l}</MenuItem>
                ))}
              </Select>
            </FormControl>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Tooltip title="This feature is coming soon">
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {}} // No-op: functionality preserved but disabled
                disabled={true}
                startIcon={<GitCompareArrows size={16} />}
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }}
              >
                Compare
              </Button>
            </span>
          </Tooltip>

          <Tooltip title="View Audit Trail">
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={navToAudit}
                startIcon={<FileClock size={16} />}
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                }}
              >
                Audit
              </Button>
            </span>
          </Tooltip>

          <Tooltip title="This feature is coming soon">
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {}} // No-op: functionality preserved but disabled
                disabled={true}
                startIcon={<ClipboardCheck size={16} />}
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }}
              >
                Checklist
              </Button>
            </span>
          </Tooltip>

          {onCancel && (
            <Button
              variant="text"
              size="small"
              onClick={onCancel}
              disabled={modifiedCount === 0}
              sx={{
                height: 32,
                fontSize: '0.875rem',
                textTransform: 'none',
                color: 'error.main',
                ...(modifiedCount === 0 && { opacity: 0.5 })
              }}
            >
              Cancel
            </Button>
          )}

          <PermissionGate require={CAPABILITIES.WYSIWYG_SAVE} mode="disable">
            <Button
              variant="contained"
              size="small"
              onClick={async () => {
                if (!onSave) return;
                try { setSaving(true); onSavingChange?.(true); await onSave(); } finally { setSaving(false); onSavingChange?.(false); }
              }}
              disabled={!templateKey || saving || modifiedCount === 0}
              startIcon={<Save size={16} />}
              sx={(theme) => ({
                height: 32,
                fontSize: '0.875rem',
                textTransform: 'none',
                minWidth: 100,
                ...(modifiedCount === 0 && {
                  bgcolor: alpha(theme.palette.primary.main, 0.3), // Bluish background with reduced opacity
                  color: alpha(theme.palette.primary.main, 0.5), // Bluish text color
                  opacity: 0.6,
                  cursor: 'not-allowed',
                  filter: 'blur(0.5px)',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.3), // Prevent hover effect
                  }
                })
              })}
            >
              {saving ? 'Saving…' : `Save${modifiedCount > 0 ? ` (${modifiedCount})` : ''}`}
            </Button>
          </PermissionGate>
        </Box>
      </Box>

      {/* Preview Header Section - Lower Section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1, // gap-2 in Tailwind = 8px = gap: 1 in MUI
          border: theme => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 1, // p-2 in Tailwind = 8px = p: 1 in MUI
          bgcolor: 'background.paper',
          flex: 1,
          minWidth: 0
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={templateChannel} 
              size="small"
              color="primary"
              sx={{ 
                fontWeight: 600,
                bgcolor: 'primary.main',
                color: 'primary.contrastText'
              }}
            />
            {templateChannel !== 'Web' && fuelType && (
              <Tooltip
                title={
                  isHerDfherMonthlyReportTemplateKey(templateKey)
                    ? 'Dual Fuel'
                    : FUEL_TYPE_FULL_NAME[fuelType] ?? fuelType
                }
              >
                <Chip
                  label={getHerFuelChipLabel(templateKey, fuelType)}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.75rem',
                    height: 20,
                    width: 70,
                    px: 0.5,
                    py: 0.5,
                    cursor: 'default',
                    borderColor: theme => alpha(theme.palette.primary.main, 0.5),
                    color: 'primary.main',
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                    '& .MuiChip-label': {
                      textAlign: 'center',
                      width: '100%',
                      px: 0
                    }
                  }}
                />
              </Tooltip>
            )}
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
              {displayTitle}
            </Typography>
          </Box>
          {templateChannel !== 'HER' && onPreviewModeChange && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                border: theme => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 0.5,
                bgcolor: 'background.default'
              }}
            >
              <Button
                variant={previewMode === 'desktop' ? 'contained' : 'text'}
                size="small"
                onClick={() => onPreviewModeChange('desktop')}
                sx={{
                  minWidth: 28, // h-7 w-7 equivalent (28px)
                  height: 28,
                  p: 0
                }}
              >
                <Monitor size={16} />
              </Button>
              <Button
                variant={previewMode === 'mobile' ? 'contained' : 'text'}
                size="small"
                onClick={() => onPreviewModeChange('mobile')}
                sx={{
                  minWidth: 28, // h-7 w-7 equivalent (28px)
                  height: 28,
                  p: 0
                }}
              >
                <Smartphone size={16} />
              </Button>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={hierarchyDisplayText} 
              size="small"
              variant="outlined"
            />
            <Chip 
              label={localeDisplayText} 
              size="small"
              variant="outlined"
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={elementKey}
                onChange={(e) => onElementChange(e.target.value as string)}
                disabled={disabled || !elements.length}
                displayEmpty
                  sx={{
                    height: 32,
                    fontSize: '0.875rem',
                    '& .MuiSelect-select': {
                      py: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: '0.875rem'
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 400,
                        zIndex: 1300, // Ensure menu appears above other elements
                        '& .MuiMenuItem-root': {
                          fontSize: '0.875rem'
                        }
                      }
                    },
                    MenuListProps: {
                      sx: {
                        maxHeight: 400,
                        overflow: 'auto'
                      }
                    },
                    disableAutoFocus: false,
                    disableEnforceFocus: false,
                    disableRestoreFocus: false
                  }}
                  renderValue={(value) => {
                    const selected = (elements.length ? elements : [{ elementKey: 'Full Template', displayName: 'Full Template' }])
                      .find(e => e.elementKey === value);
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Library size={16} style={{ color: 'inherit', opacity: 0.7 }} />
                        <span style={{ fontSize: '0.875rem' }}>{selected?.displayName || value}</span>
                      </Box>
                    );
                  }}
                >
                  {(elements.length ? elements : [{ elementKey: 'Full Template', displayName: 'Full Template' }]).map(e => (
                    <MenuItem 
                      key={e.elementKey} 
                      value={e.elementKey} 
                      sx={{ 
                        fontSize: '0.875rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      {e.elementKey === 'Full Template' && <Library size={16} style={{ marginRight: '8px' }} />}
                        <span>{e.displayName}</span>
                      </Box>
                      {signedOffElements.has(e.elementKey) && (
                        <CheckCheck size={16} style={{ color: '#16a34a', flexShrink: 0 }} />
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

            <FormControl
              size="small"
              sx={{ minWidth: 180 }}
              error={!!scenariosError}
            >
              <Select
                value={(() => {
                  // Ensure the value matches one of the available scenario values
                  const currentValue = variant.scenario || '';
                  
                  // If no scenarios available, return empty string or current value
                  if (scenarios.length === 0) {
                    return currentValue || '';
                  }
                  
                  // Check if current value exists in available scenarios
                  const hasMatchingValue = scenarios.some(ds => ds.scenarioId === currentValue);
                  
                  if (!hasMatchingValue) {
                    // If current value doesn't match, try to find default scenario first
                    const defaultScenario = scenarios.find(ds => ds.defaultScenario === true);
                    if (defaultScenario) {
                      return defaultScenario.scenarioId;
                    }
                    // Otherwise use the first scenario
                    return scenarios[0]?.scenarioId || '';
                  }
                  
                  return currentValue;
                })()}
                onChange={(e) => {
                  // Store the scenarioId (e.g., "MS_1") in variant.scenario, which will be sent to API
                  // The scenarioDisplayName (e.g., "Default") is only for display in the UI
                  const selectedValue = e.target.value as string;
                  if (selectedValue) {
                    onVariantChange({ ...variant, scenario: selectedValue });
                  }
                }}
                displayEmpty
                disabled={disabled || scenariosLoading || scenarios.length === 0}
                sx={{
                  height: 32,
                  fontSize: '0.875rem',
                  '& .MuiSelect-select': {
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '0.875rem'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 400,
                      zIndex: 1300, // Ensure menu appears above other elements
                      '& .MuiMenuItem-root': {
                        fontSize: '0.875rem'
                      }
                    }
                  },
                  MenuListProps: {
                    sx: {
                      maxHeight: 400,
                      overflow: 'auto'
                    }
                  },
                  disableAutoFocus: false,
                  disableEnforceFocus: false,
                  disableRestoreFocus: false
                }}
                renderValue={(value) => {
                  // Note: 'value' parameter is the scenarioId (e.g., "MS_1"), but we display the scenarioDisplayName (e.g., "Default")
                  if (scenariosLoading) {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={14} />
                        <span style={{ fontSize: '0.875rem', opacity: 0.7 }}>Loading...</span>
                      </Box>
                    );
                  }
                  const selected = scenarios.find(ds => ds.scenarioId === value);
                  if (!selected && scenarios.length === 0) {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Layers size={16} style={{ color: 'inherit', opacity: 0.5 }} />
                        <span style={{ fontSize: '0.875rem', opacity: 0.7 }}>
                          {scenariosError ? 'Error loading' : 'No scenarios'}
                        </span>
                      </Box>
                    );
                  }
                  // Display scenarioDisplayName in UI, but scenarioId (e.g., "MS_1") is stored in variant.scenario and sent to API
                  const displayName = selected?.scenarioDisplayName || selected?.scenarioId || value;
                  return (
                    <Tooltip title={truncateText(displayName, 14) !== displayName ? displayName : ''} placement="top">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Layers size={16} style={{ color: 'inherit', opacity: 0.7 }} />
                      <span style={{ fontSize: '0.875rem' }}>{truncateText(displayName, 14)}</span>
                    </Box>
                    </Tooltip>
                  );
                }}
              >
                {scenariosLoading ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <CircularProgress size={14} />
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>Loading scenarios...</Typography>
                    </Box>
                  </MenuItem>
                ) : scenarios.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                      {scenariosError ? 'Error loading scenarios' : 'No scenarios available'}
                    </Typography>
                  </MenuItem>
                ) : (
                  [
                    ...scenarios.map((ds, index) => (
                      <MenuItem 
                        key={`${ds.scenarioId}-${index}`}
                        value={ds.scenarioId} 
                        sx={{ 
                          fontSize: '0.875rem',
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap'
                        }}
                        title={truncateText(ds.scenarioDisplayName || ds.scenarioId, 14) !== (ds.scenarioDisplayName || ds.scenarioId) ? (ds.scenarioDisplayName || ds.scenarioId) : ''}
                      >
                        {ds.scenarioDisplayName || ds.scenarioId}
                      </MenuItem>
                    )),
                    <Divider key="divider" />,
                    <MenuItem 
                      key="manage-scenarios"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.location.href = '/content-management/data-scenarios';
                      }}
                      sx={{ 
                        fontSize: '0.875rem',
                        color: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        py: 1,
                        px: 2,
                        '&:hover': {
                          textDecoration: 'underline',
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <PlusCircle size={16} />
                      <span>Manage Scenarios...</span>
                    </MenuItem>
                  ]
                )}
              </Select>
              {scenariosError && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, ml: 1.75 }}>
                  <Typography variant="caption" sx={{ color: 'error.main' }}>
                    {scenariosError}
                  </Typography>
                  {onRetryScenarios && (
                    <Button size="small" variant="text" color="error" sx={{ minWidth: 0, px: 0.5, py: 0 }} onClick={onRetryScenarios}>
                      Retry
                    </Button>
                  )}
                </Box>
              )}
            </FormControl>
          </Box>
        </Box>
      </Box>
    </>
  );
}
